import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { useIntl, FormattedMessage } from 'react-intl';
import { Container, Paper, Typography } from '@mui/material';

import useInsuranceInfo from '../hooks/useInsuranceInfo';
import {
  INSURANCE_CATEGORY_DENTAL,
  INSURANCE_CATEGORY_PET_HEALTH,
  INSURANCE_CATEGORY_PET_LIABILITY,
} from '../shared';
import {
  DENTOLO_STEP_MAIN,
  PETOLO_STEP_MAIN,
  PET_LIABILITY_STEP_MAIN,
} from '../features/claims/new/newClaimsFormSteps';

import { PageTitle } from './common';
import InfoWindow from './InfoWindow';

const getTryAgainUrl = (insuranceCategory) => {
  switch (insuranceCategory) {
    case INSURANCE_CATEGORY_DENTAL:
      return DENTOLO_STEP_MAIN;

    case INSURANCE_CATEGORY_PET_HEALTH:
      return PETOLO_STEP_MAIN;

    case INSURANCE_CATEGORY_PET_LIABILITY:
      return PET_LIABILITY_STEP_MAIN;

    default:
      return DENTOLO_STEP_MAIN;
  }
};

const getInfoWindowSuccessTitleId = (insuranceCategory) => {
  switch (insuranceCategory) {
    case INSURANCE_CATEGORY_DENTAL:
    case INSURANCE_CATEGORY_PET_HEALTH:
      return 'file.upload.success.title.html';

    case INSURANCE_CATEGORY_PET_LIABILITY:
      return 'file.upload.pet_liability.success.title.html';

    default:
      return 'file.upload.success.title.html';
  }
};

const getInfoWindowSuccessText = (insuranceCategory, filesNum) => {
  switch (insuranceCategory) {
    case INSURANCE_CATEGORY_DENTAL:
      return filesNum === '1'
        ? 'file.upload.success.text.html.singular.delay'
        : 'file.upload.success.text.html.plural.delay';
    case INSURANCE_CATEGORY_PET_HEALTH:
      return filesNum === '1'
        ? 'file.upload.success.text.html.singular.delay'
        : 'file.upload.success.text.html.plural.delay';
    case INSURANCE_CATEGORY_PET_LIABILITY:
      return 'file.upload.pet_liability.success.text.html';
    default:
      return filesNum === '1'
        ? 'file.upload.success.text.html.singular'
        : 'file.upload.success.text.html.plural';
  }
};

export default function FileUploadStatus() {
  const intl = useIntl();

  const { status } = useParams();
  const { category: insuranceCategory } = useInsuranceInfo();

  const { search } = useLocation();
  const params = queryString.parse(search);

  const filesNum = Number.isInteger(parseInt(params?.count, 10)) ? params.count : '0';

  const isSuccess = status === 'success';
  const infoWindowMode = isSuccess ? 'success' : 'failure';
  const infoWindowTitle = intl.formatMessage({
    id: isSuccess
      ? getInfoWindowSuccessTitleId(insuranceCategory)
      : 'file.upload.failure.title.html',
  });

  const infoWindowSuccessText = intl.formatMessage(
    {
      id: getInfoWindowSuccessText(insuranceCategory, filesNum),
    },
    { filesNum }
  );

  const infoWindowFailureText = intl.formatMessage({ id: 'file.upload.failure.text.html' });

  const infoWindowSuccessButton = {
    text: intl.formatMessage({ id: 'file.upload.success.back.home.btn' }),
    href: '/',
  };
  const infoWindowFailureButtons = [
    {
      text: intl.formatMessage({ id: 'file.upload.failure.cancel.btn' }),
      href: '/',
      style: 'outlined',
    },
    {
      text: intl.formatMessage({ id: 'file.upload.failure.try.again.btn' }),
      href: getTryAgainUrl(insuranceCategory),
      style: 'contained',
    },
  ];

  return (
    <Container>
      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage id="file.upload.title" />
        </Typography>
      </PageTitle>

      <Paper>
        <InfoWindow
          mode={infoWindowMode}
          titleHtml={infoWindowTitle}
          textHtml={isSuccess ? infoWindowSuccessText : infoWindowFailureText}
          button={isSuccess ? infoWindowSuccessButton : null}
          buttons={!isSuccess ? infoWindowFailureButtons : null}
        />
      </Paper>
    </Container>
  );
}
